import _ from 'lodash';
import moment from 'moment';
import config from 'client/shared/config';

import {
	observable,
	action,
	computed,
	runInAction,
	reaction,
	toJS
} from 'mobx';

import BaseStore from './base-store';
import apiStore from './api-store';
import userStore from './user-store';
import Artist from './artist';

import {
	TIERS,
	RATING_CATEGORIES,
	RATING_SCALE
} from './artist-ratings-constants';

const { routes } = config;

class ArtistStore extends BaseStore {
	// GET path used by BaseStore.fetch / fetchIfEmpty — the member BFF list,
	// which is PII-suppressed. The write path (patchArtist, saveArtist) still
	// hits the generic /api/artists/:id (gated to admin + self) via
	// `writeRoute` below.
	apiRoute = routes.bffMembersArtists;
	writeRoute = routes.artists;

	status = {
		PENDING: 'PENDING',
		ACTIVE: 'ACTIVE',
		INACTIVE: 'INACTIVE',
		CANCELLED: 'CANCELLED'
	};

	constructor () {
		super();

		this.disposeReaction = reaction(
			() => apiStore.data.artist,
			this.onFetchSuccess
		);

		this.disposeSearchReaction = reaction(
			() => this.searchTerm,
			this.search
		);
	}

	/**
	 * Observables & Computeds
	 */

	@observable
	_ratingCategories = RATING_CATEGORIES.map((label) => {
		return { label, score: 0 };
	});

	@computed
	get ratingCategories () {
		return this._ratingCategories;
	}

	set ratingCategories (ratingCategories) {
		this._ratingCategories = ratingCategories;
	}

	@observable
	_ratingScale = RATING_SCALE;

	@computed
	get ratingScale () {
		return this._ratingScale;
	}

	set ratingScale (ratingScale) {
		this._ratingScale = ratingScale;
	}

	@observable
	_statusFilter = {
		status: this.status.ACTIVE
	};

	@computed
	get statusFilter () {
		return this._statusFilter;
	}

	set statusFilter (status) {
		this._statusFilter = { status };
	}

	@observable
	_tierFilter = {
		tier: TIERS.TIER_1
	};

	@computed
	get tierFilter () {
		return this._tierFilter;
	}

	set tierFilter (tierKey) {
		const tier = TIERS[tierKey] || TIERS.TIER_1;
		this._tierFilter = tierFilter;
	}

	@observable
	_searchTerm = '';

	@computed
	get searchTerm () {
		return this._searchTerm;
	}

	set searchTerm (searchTerm) {
		this._searchTerm = searchTerm;
	}

	@observable
	_searchResults = [];

	@computed
	get searchResults () {
		return this._searchResults;
	}

	set searchResults (searchResults) {
		this._searchResults = searchResults;
	}

	@computed
	get artists () {
		return this._items;
	}

	set artists (artists) {
		this._items = artists;
	}

	@observable
	_loggedInArtist = {};

	@computed
	get loggedInArtist () {
		return this._loggedInArtist;
	}

	set loggedInArtist (loggedInArtist) {
		this._loggedInArtist = loggedInArtist;
	}

	@computed
	get pendingArtists () {
		if (_.isEmpty(this.artistUserList)) {

			return [];
		}

		return this.artistUserList
			.filter(({ status }) => status === this.status.PENDING);
	}

	@computed
	get submissionsList () {
		const categorizedList = {};

		this.pendingArtists.forEach((artistUser) => {
			const regDate = moment(artistUser.createdAt).format('MM/DD/YYYY');

			if (_.isEmpty(categorizedList[regDate])) {

				categorizedList[regDate] = [];
			}

			categorizedList[regDate].push(artistUser);
		});

		return categorizedList;
	}

	@computed
	get filteredArtistList () {
		const { statusFilter = {}, tierFilter = {} } = this;

		const filteredArtistList = _.filter(
			this.artists,
			{
				...toJS(statusFilter),
				...toJS(tierFilter)
			}
		);

		return filteredArtistList;
	}

	@computed
	get alphabeticArtists () {
		return _(this.artists)
			.filter(({ status }) => status === this.status.ACTIVE)
			.sortBy('name')
			.value();
	}

	@computed
	get featuredArtists () {
		return this.getFeaturedArtists();
	}

	@computed
	get topSelling () {
		return [];
	}

	@computed
	get trending () {
		return [];
	}

	@computed
	get mostHiFived () {
		return [];
	}

	getFeaturedArtists () {
		return _(this.artists)
			.filter(({ isFeatured }) => isFeatured === true)
			.value();
	}

	@computed
	get newReleases () {
		return _(this.artists)
			.filter(({ status }) => status === this.status.ACTIVE)
			.sortBy('createdAt')
			.reverse()
			.slice(0, 9)
			.value();
	}

	/**
	 * Actions
	 */

	@action.bound
	onFetchSuccess (data) {
		this.isLoading = false;

		if (_.isEmpty(data)) {

			return;
		}

		const artistData = _.isArray(data) ? data : [ data ];

		this.artists = artistData.map((artist) => {
			const existing = this.getItemByID(artist.id);

			if (_.isEmpty(existing)) {

				return new Artist(this, artist);
			}

			existing.update(artist);
			return existing;
		});
	}

	@action
	async getArtistProfile ({ songsStore, albumsStore, userStore }) {
		this.isLoading = true;

		try {

			const route = config.getRoute('profile');			
			const data = await apiStore.fetch({ route });

			runInAction('Update with artist profile', () => {
				apiStore.userID = data.user.id;
				this.loggedInArtist = this.newArtist(data.artist);
				userStore.onFetchSuccess(data.user);
				songsStore.onFetchSuccess(this.loggedInArtist.songs);
				albumsStore.onFetchSuccess(this.loggedInArtist.albums);
			});
		} catch (error) {

			console.log(error);
		} finally {

			this.isLoading = false;
		}
	}

	@action
	async saveArtist (artist, opts = {}) {
		// Reads/list go through the BFF (this.apiRoute). Writes MUST target
		// the generic /api/artists — the BFF list endpoint is GET-only.
		const route = _.isEmpty(artist.id) ? routes.createArtist : this.writeRoute;
		await this.save(artist, { route });
		return artist;
	}

	/**
	 * Surgical PATCH — sends only the fields you pass; every other field on
	 * the artist document is untouched. Use in place of artist.save() when
	 * only a few fields changed (e.g. updating bio shouldn't transmit the
	 * whole artist payload, which has historically corrupted data on save).
	 *
	 * API now inline-refreshes searchStatus on patches that affect it (see
	 * ArtistController.updateItem). If the response carries a fresh
	 * searchStatus, merge it onto the local artist so the UI flips without
	 * a refetch.
	 *
	 * @param {string} id     Artist ObjectId
	 * @param {Object} fields Subset of fields to update
	 */
	@action
	async patchArtist (id, fields) {
		if (_.isEmpty(id) || _.isEmpty(fields)) {
			return;
		}
		const response = await apiStore.save({
			route: this.writeRoute,   // PATCH must target /api/artists/:id, not the BFF list
			payload: { _id: id, ...fields },
			method: 'patch',
		});

		if (response?.searchStatus) {
			this.applyLinkedArtistSearchStatus({
				artistID: id,
				searchStatus: response.searchStatus,
			});
		}

		return response;
	}

	/**
	 * Merge a fresh `searchStatus` from the server onto the local artist
	 * (loggedInArtist + any matching entry in `items`) WITHOUT a refetch.
	 *
	 * Callers: anywhere a server response carries either `searchStatus`
	 * directly (artist-scoped routes) or `linkedArtistSearchStatus`
	 * (user/song/image routes that affect the artist via the Atlas trigger).
	 *
	 * @param {Object} payload   { artistID, searchStatus }
	 */
	@action
	applyLinkedArtistSearchStatus (payload) {
		if (!payload?.artistID || !payload?.searchStatus) return;

		const targetId = String(payload.artistID);

		if (String(this._loggedInArtist?.id) === targetId) {
			this._loggedInArtist.searchStatus = payload.searchStatus;
		}

		const match = this._items.find(a => String(a.id) === targetId);
		if (match) {
			match.searchStatus = payload.searchStatus;
		}
	}

	@action
	async createArtist ({ artist, user }) {
		this.isLoading = true;

		try {

			const formData = new FormData();
			const route = routes.createArtist;

			formData.append('s3Path', artist.songDirectory);
			formData.append('imageData', user.profileImageData);
			formData.append('bannerImageData', artist.bannerImageData);
			user.profileImageData = null;
			artist.bannerImageData = null;

			formData.append('artist', artist.jsonString);
			formData.append('user', user.jsonString);

			await this.save(formData, { route });
		} catch (err) {

			this.isLoading = false;
			throw err;
		}
	}

	@action.bound
	async saveBannerImage (artist) {
		const { id, bannerImageData } = artist;
		const route = config.getRoute('bannerImageUpload', id);
		const formData = new FormData();

		formData.append('bannerImageData', bannerImageData);
		formData.append('fileName', 'banner-image');
		formData.append('path', `banner-images/${id}`);

		try {

			const response = await this.save(formData, { route, silent: false });
			const { path, searchStatus } = response || {};
			console.log('[artist-store->saveBannerImage] path', path);
			runInAction('Update with saved banner image', () => {
				artist.bannerImage = path;
				artist._bannerImageCacheBust = Date.now();
			});

			// API attaches fresh searchStatus on banner upload — apply to
			// loggedInArtist (or items) so the CTA flips without a refetch.
			if (searchStatus) {
				this.applyLinkedArtistSearchStatus({ artistID: id, searchStatus });
			}
		} catch (err) {

			console.error('[ERROR: ArtistStore.saveBannerImage]', err);
		}
	}

	@action.bound
	async saveMobileBannerImage (artist) {
		const { id, mobileBannerImageData } = artist;
		const route = config.getRoute('mobileBannerImageUpload', id);
		const formData = new FormData();

		// Server reuses the `bannerImageData`/`newBannerImageUpload` body key
		// from the existing handleImageUpload chain — only the route differs.
		formData.append('bannerImageData', mobileBannerImageData);
		formData.append('fileName', 'mobile-banner-image');
		formData.append('path', `banner-images/${id}/mobile`);

		try {
			const response = await this.save(formData, { route, silent: false });
			const { path } = response || {};
			runInAction('Update with saved mobile banner image', () => {
				artist.mobileBannerImage = path;
				artist._mobileBannerImageCacheBust = Date.now();
			});
		} catch (err) {
			console.error('[ERROR: ArtistStore.saveMobileBannerImage]', err);
		}
	}

	@action
	async saveArtistPhoto (artist, imageData) {
		const route = routes.artistPhotoUpload.replace(/\:id/, artist.id);
		const payload = {
			imgData: imageData,
			path: `band-photos/${userStore.userNameForFilePath}`
		};

		try {

			const data = await this.save(payload, { route });
			artist.update(data);
		} catch (err) {

			console.log('[ERROR: ArtistStore.saveArtistPhoto]', err);
		}
	}

	@action
	async approveArtist (artist) {
		artist.status = this.status.ACTIVE;
		await this.save(artist);
		await this.fetch();
	}

	@action
	async addFeaturedArtist (artist) {
		const featuredArtists = this.getFeaturedArtists();

		artist.featuredPosition = featuredArtists.length > 0 ? featuredArtists.length : 0;

		await this.save(artist, { silent: true });
		await this.fetch();
	}

	@action
	async removeFeaturedArtist (artist) {
		artist.featuredPosition = -1;

		await this.save(artist, { silent: true });
		await this.fetch();
	}

	@action
	async swapFeaturedPosition ({ removeArtist, addArtist }) {
		addArtist.featuredPosition = removeArtist.featuredPosition;
		removeArtist.featuredPosition = -1;

		await this.save(addArtist, { silent: true });
		await this.save(removeArtist, { silent: true });
		await this.fetch();
	}

	@action
	newArtist (data = {}, user = {}) {
		const artist = new Artist(this, { user, ...data });
		this.artists.unshift(artist);

		return artist;
	}

	@action.bound
	search () {
		if (_.isEmpty(this.searchTerm) || this.searchTerm.length < 3) {

			this.searchResults = [];
			return;
		}

		const re = new RegExp(this.searchTerm, 'i');
		this.searchResults = this.artists.filter((artist) => re.test(artist.name) && artist.isSearchable);
	}

	/**
	 * Methods
	 */

	getArtistsByGenre (id) {
		return _.filter(this.artists, ({ genre }) => genre._id === id);
	}

	getArtistByID (id) {
		return  this.getItemByID(id);
	}

	async retrieveConnectAccount (id) {
		try {
			const route = config.getRoute('retrieveConnectAccount').replace(/\:id$/, id);;
			const data = await apiStore.ax.get(route);
			console.log(data);
			return data;
		} catch (err) {
			console.error('[ERROR: Artist.retrieveConnectAccount]', err);
		}
	}

	async createConnectAccount (artistID) {
		try {
			const route = config.getRoute('createConnectAccount');
			const data = await apiStore.ax.post(route, { artistID });
			console.log(data);
			return data;
		} catch (err) {
			console.error('[ERROR: Artist.createConnectAccount]', err);
		}
	}

	async createAccountLink (userID, artistID) {
		try {
			const route = config.getRoute('createAccountLink').replace(/\:id$/, userID);
			const data = await apiStore.ax.post(route, { id: userID, artistID });
			console.log(data);
			return data;
		} catch (err) {
			console.error('[ERROR: Artist.createAccountLink]', err);
		}
	}

	async typeAheadSearch (search) {
		if (search.length < 3) {

			return [];
		}

		const route = routes.searchGenres.replace(/\:search/, search),
			response = await apiStore.ax.get(route);

		return response.data.sort(function(a, b) {
			const labelA = a.label.toUpperCase();
			const labelB = b.label.toUpperCase();

			if (labelA < labelB) {

				return -1;
			}

			if (labelA > labelB) {

				return 1;
			}

			return 0;
		});
	}

	getArtistByUserID (userID) {
		return _.first(this.artists.filter((artist) => artist.userID === userID));
	}
}

const artistStore = new ArtistStore();
export default artistStore;
